import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from "react";

interface DocumentReviewContextType {
  documentsToReview: string[];
  setDocumentsToReview: (documentIds: string[]) => void;
  clearDocumentsToReview: () => void;
  /** Uploads whose opener pre-checked "Use this document on future reports"; the review starts checked. */
  carryForwardDocumentIds: ReadonlySet<string>;
  markCarryForward: (documentIds: string[]) => void;
}

const DocumentReviewContext = createContext<DocumentReviewContextType | undefined>(undefined);

interface DocumentReviewProviderProps {
  children: ReactNode;
}

export function DocumentReviewProvider({ children }: DocumentReviewProviderProps) {
  const [documentsToReview, setDocumentsToReview] = useState<string[]>([]);
  const [carryForwardDocumentIds, setCarryForwardDocumentIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const clearDocumentsToReview = useCallback(() => {
    setDocumentsToReview([]);
  }, []);

  const markCarryForward = useCallback((documentIds: string[]) => {
    setCarryForwardDocumentIds((previous) => new Set([...previous, ...documentIds]));
  }, []);

  const contextValue = useMemo(
    () => ({
      documentsToReview,
      setDocumentsToReview,
      clearDocumentsToReview,
      carryForwardDocumentIds,
      markCarryForward,
    }),
    [documentsToReview, clearDocumentsToReview, carryForwardDocumentIds, markCarryForward]
  );

  return (
    <DocumentReviewContext.Provider value={contextValue}>{children}</DocumentReviewContext.Provider>
  );
}

export function useDocumentReview(): DocumentReviewContextType {
  const context = useContext(DocumentReviewContext);
  if (context === undefined) {
    throw new Error("useDocumentReview must be used within a DocumentReviewProvider");
  }
  return context;
}
